import { PRICING_PLANS } from '../data/content';
import { Check } from 'lucide-react';

interface PlanesSectionProps {
  onOpenDiagnostic: (planId?: string) => void;
}

export default function PlanesSection({ onOpenDiagnostic }: PlanesSectionProps) {
  return (
    <section id="planes" className="py-20 md:py-28 bg-[#f2f0eb] dark:bg-[#070e17] text-slate-900 dark:text-white relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left max-w-3xl space-y-3">
          <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 tracking-widest uppercase">PLANES DE NEGOCIO</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
            Un plan para cada etapa de <br className="hidden sm:inline" />
            tu marca
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
            Valores mensuales referenciales en pesos chilenos. Todos los planes se adaptan a tus objetivos.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {PRICING_PLANS.map((plan) => {
            const isDarkGreen = plan.isPopular;
            const isDarkNavy = plan.id === 'reputacion-asuntos-publicos';

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-5 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-xl ${
                  isDarkGreen
                    ? 'bg-[#1b5037] text-white shadow-lg border-2 border-emerald-500'
                    : isDarkNavy
                    ? 'bg-[#123144] text-white border border-slate-700'
                    : 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200/80 dark:border-slate-800'
                }`}
              >
                <div>
                  {/* Top Badge */}
                  {plan.badge && (
                    <div className="mb-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${
                          isDarkGreen
                            ? 'bg-emerald-400 text-slate-950'
                            : isDarkNavy
                            ? 'bg-cyan-400 text-slate-950'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  {/* Plan Title */}
                  <h3 className={`text-2xl font-black tracking-tight ${isDarkGreen || isDarkNavy ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                    {plan.title}
                  </h3>

                  {/* Price */}
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl sm:text-4xl font-black tracking-tight">{plan.price}</span>
                    <span className={`text-xs font-semibold ${isDarkGreen || isDarkNavy ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
                      {plan.period}
                    </span>
                  </div>

                  {/* Features List */}
                  <div className="mt-8 space-y-3 border-t border-slate-200/60 dark:border-slate-800 pt-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-xs sm:text-sm font-medium">
                        <div
                          className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                            isDarkGreen
                              ? 'bg-emerald-400/20 text-emerald-300'
                              : isDarkNavy
                              ? 'bg-cyan-400/20 text-cyan-300'
                              : 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                          }`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </div>
                        <span className={isDarkGreen || isDarkNavy ? 'text-slate-200' : 'text-slate-700 dark:text-slate-300'}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plan CTA Button */}
                <div className="mt-8 pt-6 border-t border-slate-200/60 dark:border-slate-800">
                  <button
                    onClick={() => onOpenDiagnostic(plan.id)}
                    className={`w-full py-3.5 px-6 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 ${
                      isDarkGreen
                        ? 'bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-black'
                        : isDarkNavy
                        ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black'
                        : 'bg-slate-900 hover:bg-slate-800 dark:bg-cyan-600 dark:hover:bg-cyan-500 text-white font-bold'
                    }`}
                  >
                    {plan.ctaText}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
